import { css, styled } from 'styled-components';

import type { ChatConversation } from '../../../../../common';
import Document from '../document';
import Feedback from '../feedback';
import MessageContent, { getMessageContent } from '../messageContent';
import { fadeIn } from '../../../../../styles/animation';
import StaticMap from '../staticMap';
import ProviderCards from './providerCards';
import { isEnumMember } from 'common/dist/utils/types';
import { INTENT_WITH_PANEL } from '../Intent';

import * as api from '../../../../../api';

interface Props {
  message: ChatConversation;
  showTextAnimation: boolean;
}

const BaseMessage: React.FC<Props> = ({ message, showTextAnimation }) => {
  const sourceDocuments =
    'source_documents' in message
      ? message.source_documents
          .filter((source) => source.metadata.url)
          .filter(
            (source, index, self) =>
              index ===
              self.findIndex((s) => s.metadata.url === source.metadata.url),
          )
      : [];

  const isIntentWithPanel =
    'intent' in message &&
    isEnumMember(api.Assistant.Intent, message.intent) &&
    INTENT_WITH_PANEL[message.intent];

  return (
    <div>
      <MessageWrapper $isAgent={'answer' in message}>
        <MessageDisplay>
          <MessageContent
            content={getMessageContent(message)}
            showTextAnimation={showTextAnimation}
            isAgent={'answer' in message}
            isLoading={!message.done}
          />
        </MessageDisplay>
        {'answer' in message && message.answer.length ? (
          <Feedback
            showTextAnimation={showTextAnimation}
            message={message}
            showDesktopView={true}
          />
        ) : null}
      </MessageWrapper>
      {'response_entities' in message &&
        message.response_entities.staticMap &&
        message.response_entities.address && (
          <StaticMap
            address={message.response_entities.address}
            staticMap={message.response_entities.staticMap}
            showTextAnimation={showTextAnimation}
            messageId={message.chat_message_id}
          />
        )}
      {!isIntentWithPanel ? <ProviderCards message={message} /> : null}
      {!!sourceDocuments.length && message.done && (
        <>
          <LinkWrapperLabel>Related links</LinkWrapperLabel>
          <LinkWrapper $showTextAnimation={showTextAnimation} $gap={5}>
            {sourceDocuments.map((source, i) => (
              <Document
                sourceDocument={source}
                sourceNumber={i}
                key={`${source.metadata.url}${i}`}
                messageId={message.chat_message_id}
              />
            ))}
          </LinkWrapper>
        </>
      )}
      {'answer' in message && message.answer.length ? (
        <Feedback
          showTextAnimation={showTextAnimation}
          message={message}
          showDesktopView={false}
        />
      ) : null}
    </div>
  );
};

const MessageWrapper = styled.div.attrs<{ $isAgent: boolean }>((props) => ({
  $isAgent: props.$isAgent ?? false,
}))`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: ${(props) => (props.$isAgent ? '0px' : '32px')};

  @media (max-width: 680px) {
    flex-direction: column;
  }
`;

const MessageDisplay = styled.div`
  flex-grow: 1;
`;

const LinkWrapper = styled.div.attrs<{
  $showTextAnimation: boolean;
  $gap: number;
}>((props) => ({
  $showTextAnimation: props.$showTextAnimation ?? false,
  $gap: props.$gap ?? 5,
}))`
  display: flex;
  flex-wrap: wrap;
  opacity: ${(props) => (props.$showTextAnimation ? 0 : 1)};
  gap: ${(props) => props.$gap + 'px'};
  margin-top: 10px;
  margin-bottom: 1rem;
  animation: ${(props) =>
    props.$showTextAnimation
      ? css`
          ${fadeIn} .6s ease-in-out forwards
        `
      : 'none'};

  @media (max-width: 680px) {
    margin-bottom: 0;
  }

  a {
    max-width: calc(50% - (${(props) => props.$gap + 'px'} / 2));
  }
`;

const LinkWrapperLabel = styled.div`
  color: var(--font-color);
  font-family: var(--conversation-font-family);
  font-size: 14px;
  font-weight: bold;
`;

export default BaseMessage;
